
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Button } from '../components/UI';
import { apiRequest } from '../utils/api';
import { Save, AlertTriangle, ArrowLeft, Calendar, Loader2, Plus, Trash2, FlaskConical } from 'lucide-react';
import { useLeague } from '../components/LeagueContext';
import { PageHero } from '../components/PageHero';

interface EditableEvent {
    id: string;
    trackId: string;
    trackName?: string;
    trackLocation?: string;
    date: string;
    isSprint: boolean;
    isTestRace: boolean;
    distance: string;
    roundNumber: number;
    isNew?: boolean; // UI flag
}

export const SeasonCalendarEditor: React.FC = () => {
    const { id: seasonId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { tracks } = useLeague();
    
    const [events, setEvents] = useState<EditableEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [seasonName, setSeasonName] = useState('');

    useEffect(() => {
        if (!seasonId) return;
        
        // Load events
        apiRequest<any[]>(`events.php?seasonId=${seasonId}`).then(data => {
            const mapped = data.map((e: any) => ({
                id: e.id,
                trackId: e.trackId,
                trackName: e.trackName,
                trackLocation: e.trackLocation,
                date: e.date ? e.date.replace(' ', 'T').substring(0, 16) : '', 
                isSprint: !!e.isSprint,
                isTestRace: e.isTestRace === true || e.isTestRace === '1',
                distance: e.distance,
                roundNumber: e.roundNumber
            }));
            setEvents(mapped);
            if(data.length > 0 && data[0].seasonName) setSeasonName(data[0].seasonName);
        }).catch(e => {
            alert("Error loading events: " + e.message);
        }).finally(() => setLoading(false));

    }, [seasonId]);

    const handleChange = (index: number, field: keyof EditableEvent, value: any) => {
        const newEvents = [...events];
        newEvents[index] = { ...newEvents[index], [field]: value };
        setEvents(newEvents);
    };

    const handleAddRace = () => {
        if (tracks.length === 0) return;
        const newEvent: EditableEvent = {
            id: `new_${Date.now()}`,
            trackId: tracks[0].id,
            trackName: tracks[0].name,
            trackLocation: tracks[0].location,
            date: '',
            isSprint: false,
            isTestRace: false,
            distance: '50%',
            roundNumber: 0, // Will be calculated
            isNew: true
        };
        setEvents([...events, newEvent]);
    };

    const handleRemoveRace = (index: number) => {
        if (!confirm("Rennen entfernen?")) return;
        
        const evt = events[index];
        if (evt.isNew) {
            const newEvents = events.filter((_, i) => i !== index);
            setEvents(newEvents);
        } else {
            alert("Das Löschen bestehender Rennen wird hier noch nicht unterstützt (Sicherheitsgründe). Bitte Datenbank nutzen oder auf 'Testrennen' setzen.");
        }
    };

    const handleSave = async () => {
        if (!seasonId) return;
        setSaving(true);
        try {
            await apiRequest('seasons.php', 'PUT', {
                id: seasonId,
                events: events.map(e => ({
                    id: e.isNew ? null : e.id, // Send null ID for new events
                    trackId: e.trackId, // Needed for new events
                    date: e.date,
                    isSprint: e.isSprint,
                    isTestRace: e.isTestRace,
                    distance: e.distance
                }))
            });
            alert("Kalender erfolgreich aktualisiert!");
            navigate('/admin/seasons');
        } catch (e: any) {
            alert("Fehler beim Speichern: " + e.message);
        } finally {
            setSaving(false);
        }
    };

    const getTrackInfo = (trackId: string) => {
        return tracks.find(t => t.id === trackId);
    };

    if (loading) return <div className="p-20 text-center text-gray-500 flex flex-col items-center"><Loader2 className="animate-spin mb-2"/> Lade Kalender...</div>;

    // Helper for visual Round Calculation
    let roundCounter = 1;

    return (
        <div className="max-w-7xl mx-auto animate-fade-in pb-20 space-y-6">
            <Link to="/admin/seasons" className="flex items-center text-gray-500 hover:text-white text-xs font-bold uppercase tracking-widest transition-colors">
                <ArrowLeft size={14} className="mr-1"/> Zurück zu den Saisons
            </Link>
            <PageHero
                eyebrow={seasonName || 'Saisonverwaltung'}
                title="Rennkalender bearbeiten"
                description="Rennreihenfolge, Termine, Formate und Testrennen einer Saison übersichtlich zusammenstellen."
                icon={Calendar}
                accent="#e10600"
                stats={[{ label: 'Einträge', value: events.length }, { label: 'WM-Rennen', value: events.filter(event => !event.isTestRace).length }, { label: 'Testrennen', value: events.filter(event => event.isTestRace).length }]}
                actions={(
                    <div className="flex flex-wrap gap-2">
                    <Button variant="secondary" onClick={handleAddRace} icon={Plus}>Rennen hinzufügen</Button>
                    <Button onClick={handleSave} disabled={saving} icon={Save}>
                        {saving ? 'Speichert …' : 'Alle Änderungen speichern'}
                    </Button>
                    </div>
                )}
            />

            <Card className="rounded-[1.75rem] border-white/[0.075] bg-gradient-to-br from-[#171a24] to-[#0e1017] shadow-[0_24px_70px_rgba(0,0,0,0.22)]">
                <div className="p-4 bg-yellow-900/10 border border-yellow-700/30 rounded mb-6 flex items-start gap-3">
                    <AlertTriangle className="text-yellow-500 shrink-0 mt-0.5" size={18} />
                    <div className="text-sm text-yellow-100">
                        <span className="font-bold">Achtung:</span> Testrennen zählen nicht zur WM und erhalten keine Runden-Nummer. Die Rennen werden beim Speichern chronologisch neu nummeriert.
                    </div>
                </div>

                <div className="space-y-4">
                    {events.map((evt, idx) => {
                        // Calculate visual round number
                        const displayRound = evt.isTestRace ? 'TEST' : roundCounter++;

                        return (
                            <div key={idx} className={`grid grid-cols-1 lg:grid-cols-[60px_1fr_200px_100px_100px_100px_40px] gap-4 items-center bg-gray-900 p-4 rounded border ${evt.isNew ? 'border-green-500/50 bg-green-900/10' : 'border-gray-700'} hover:border-gray-500 transition-colors`}>
                                
                                {/* Round Number */}
                                <div className={`flex flex-col items-center justify-center h-full rounded border ${evt.isTestRace ? 'bg-blue-900/20 border-blue-500/50' : 'bg-gray-800 border-gray-700'}`}>
                                    <span className="text-[10px] uppercase text-gray-500 font-bold">{evt.isTestRace ? 'Typ' : 'Rnd'}</span>
                                    <span className={`text-xl font-black ${evt.isTestRace ? 'text-blue-400' : 'text-white'}`}>
                                        {displayRound}
                                    </span>
                                </div>

                                {/* Track Info / Select */}
                                <div>
                                    <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Strecke</label>
                                    <select 
                                        className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm"
                                        value={evt.trackId}
                                        onChange={e => handleChange(idx, 'trackId', e.target.value)}
                                        disabled={!evt.isNew} 
                                    >
                                        {tracks.map(t => <option key={t.id} value={t.id}>{t.location} - {t.name}</option>)}
                                    </select>
                                </div>
                                
                                {/* Date Input */}
                                <div>
                                    <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Datum (UTC)</label>
                                    <input 
                                        type="datetime-local" 
                                        className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm focus:border-f1-red outline-none"
                                        value={evt.date}
                                        onChange={e => handleChange(idx, 'date', e.target.value)}
                                    />
                                </div>

                                {/* Sprint Toggle */}
                                <div>
                                    <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Format</label>
                                    <label className={`flex items-center justify-center gap-1 cursor-pointer p-2 rounded border transition-all h-[38px] ${evt.isSprint ? 'bg-yellow-900/20 border-yellow-500' : 'bg-gray-800 border-gray-600'}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={evt.isSprint} 
                                            onChange={e => handleChange(idx, 'isSprint', e.target.checked)}
                                            className="hidden"
                                        />
                                        <span className={`text-xs font-bold ${evt.isSprint ? 'text-yellow-500' : 'text-gray-400'}`}>
                                            {evt.isSprint ? 'SPRINT' : 'Feature'}
                                        </span>
                                    </label>
                                </div>

                                {/* TEST RACE Toggle */}
                                <div>
                                    <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Modus</label>
                                    <label className={`flex items-center justify-center gap-1 cursor-pointer p-2 rounded border transition-all h-[38px] ${evt.isTestRace ? 'bg-blue-900/20 border-blue-500' : 'bg-gray-800 border-gray-600'}`}>
                                        <input 
                                            type="checkbox" 
                                            checked={evt.isTestRace} 
                                            onChange={e => handleChange(idx, 'isTestRace', e.target.checked)}
                                            className="hidden"
                                        />
                                        {evt.isTestRace ? <FlaskConical size={14} className="text-blue-400"/> : null}
                                        <span className={`text-xs font-bold ${evt.isTestRace ? 'text-blue-400' : 'text-gray-400'}`}>
                                            {evt.isTestRace ? 'TEST' : 'RANKED'}
                                        </span>
                                    </label>
                                </div>

                                {/* Distance Select */}
                                <div>
                                    <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">Länge</label>
                                    <select 
                                        className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm focus:border-f1-red outline-none h-[38px]"
                                        value={evt.distance}
                                        onChange={e => handleChange(idx, 'distance', e.target.value)}
                                    >
                                        <option value="50%">50%</option>
                                        <option value="100%">100%</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[10px] uppercase text-gray-500 font-bold mb-1">&nbsp;</label>
                                    {evt.isNew && (
                                        <button onClick={() => handleRemoveRace(idx)} className="text-red-500 hover:text-red-400 p-2"><Trash2 size={18}/></button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>
        </div>
    );
};
